import {
  CellId,
  CommentId,
  ComponentImportCellId,
  ComponentVersionStub,
  DataConnectionId,
  GridElementId,
  GridLayoutId,
  HexId,
  HexVersionId,
  HexVersionString,
  KernelImage,
  KernelSize,
  MagicEventId,
  MagicKeyword,
  ProjectLanguage,
  ProjectTemplateForUI,
  ReviewRequestId,
  RichTextDocument,
  StaticCellId,
  typedObjectEntries,
  typedObjectKeys,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { castDraft } from "immer";
import { isArray, memoize } from "lodash";

import { WarningsFragment } from "../../mutations/import.generated";
import { EditViewType } from "../../util/projectViewTypes.js";
import { RootState } from "../store";
import { CellLocation } from "../utils/cell-location/index.js";
import { createSelector } from "../utils/index.js";

import { GridLayoutMP, hexVersionMPSelectors } from "./hexVersionMPSlice.js";

type WithCellId = {
  cellId: CellId;
};
type WithCellIdData<T> = WithCellId & {
  data: T;
};
type CellDataAction<D, T extends string = string> = PayloadAction<
  WithCellIdData<D>,
  T
>;
type CellAction = PayloadAction<WithCellId>;

export type CommentLocation = "project" | "app" | ReviewRequestId;

export enum ActionBarMode {
  MAGIC = "MAGIC",
  ADD_CELL = "ADD_CELL",
}

export type ActionBarState = {
  /**
   * Indicates whether or not the action bar is in "ADD_CELL" or "MAGIC" mode. We
   * track this separately from the cursor because we don't want changing this
   * mode to trigger the project to re-calculate its cell blocks.
   */
  mode: ActionBarMode;
  isOpen: boolean;
  location: CellLocation;
  /**
   * If true, then the action bar will not animate when closing. This gets reset
   * to the default value every time the prompt bar is opened, and should get
   * set via argument to `closeActionBar`.
   * @default false
   */
  disableCloseAnimation: boolean;
};

export interface CellPromptBarState {
  cellId: CellId | null;
}

export const STILL_THINKING_THRESHOLD = 3000; // 3sec

export type MagicExploreValidationResult = "SUCCESS" | "ERROR" | "TIMEOUT";
export interface CellMagicState {
  smartEditLoaded: boolean; // Sucessfullly loaded a Magic edit into a cell
  smartEditLoading: boolean; // Loading a Magic edit into a cell
  stillThinking: boolean; // If it's still loading
  magicEventId?: MagicEventId;
  smartEditType: MagicKeyword | undefined; // "Edit" or "Generate"
  sideBySideDiff: boolean; // If the diff view is side-by-side (default is inline)
  prompt?: RichTextDocument;
  exploreValidationLoading: boolean;
  userModifiedDiff?: string;
}

const createInitialMagicCellState = (): CellMagicState => ({
  smartEditLoaded: false,
  smartEditLoading: false,
  stillThinking: false,
  magicEventId: undefined,
  smartEditType: undefined,
  sideBySideDiff: true,
  prompt: undefined,
  exploreValidationLoading: false,
  userModifiedDiff: undefined,
});

const INITIAL_MAGIC_CELL_STATE = createInitialMagicCellState();

type CellUserModifiedDiffState = {
  [cellId: CellId]: string | undefined;
};

type EmbedDialogState = {
  open: boolean;
  staticCellId: StaticCellId | null;
};

type CellModalState = {
  fullScreen?: boolean;
  cellId: CellId;
};

type CellSidebarState = {
  cellId: CellId;
};

export type ComponentImportAddCellConfig = {
  language: ProjectLanguage;
  location: CellLocation;
};

export type DeleteTabDialogState =
  | { open: false; gridLayoutToDelete?: undefined; elementCount?: undefined }
  | {
      open: true;
      gridLayoutToDelete: GridLayoutMP;
      elementCount: number;
    };

type ComponentImportDialogState = {
  open: boolean;
  selectedComponent: ComponentVersionStub | null;
  addCellConfig: ComponentImportAddCellConfig;
};

export type ComponentUpdateVersions = {
  cellId: CellId;
  componentImportCellId: ComponentImportCellId;
  hexId: HexId;
  currentHexVersionId: HexVersionId;
  currentHexVersion: HexVersionString;
  newHexVersionId: HexVersionId;
  newHexVersion: HexVersionString;
};

type ComponentUpdateDialogState = {
  open: boolean;
  componentUpdateVersions?: ComponentUpdateVersions;
};

type NewComponentSettings = {
  projectLanguage: ProjectLanguage;
  kernelSize: KernelSize;
  kernelImage: KernelImage;
};

type ComponentSidebarState = {
  open?: boolean;
  settings?: NewComponentSettings;
};

type CreateComponentSidebar = Record<
  HexVersionId,
  ComponentSidebarState | undefined
>;

export type JumpState = {
  pos: number;
  history: CellId[];
  isPanelOpen: boolean;
};

export type ReviewRequestDialogState =
  | {
      status: "initial" | "loading" | "success";
      errorMessage?: undefined;
    }
  | {
      status: "error";
      errorMessage: string;
    };

export interface PublishDialogState {
  view: "settings" | "reviewRequest";
  versionName: string;
}

export type SearchTermDecoration = {
  cellId: CellId;
  lineIndex: number;
  match: {
    startIndex: number;
    endIndex: number;
  };
};

/**
 * The state of the project when there is a non-empty search term in the project search bar.
 * Tracks both the search term and selected (active) search term.
 */
export interface ProjectSearchState {
  /**
   * The current term that the user is searching in the logic view.
   */
  projectSearchTerm: string;
  // case match and whole word match represent the search config options in the sidebar.
  caseMatch: boolean;
  wholeWordMatch: boolean;
  /**
   * In project search mode, the user can cycle through the search results.
   * If there is a currently selected search result, include this as the focusedCellState
   * to provide additional cell highlighting.
   */
  selectedSearchItem?: SearchTermDecoration;
  /**
   * In project search mode, set the item that the user's cursor is hovering over.
   */
  activeHoverItem?: SearchTermDecoration;
}

type LogicViewSliceState = {
  embedDialog: EmbedDialogState;
  deleteTabDialog: DeleteTabDialogState;
  reviewRequestDialog: ReviewRequestDialogState;
  publishDialog: PublishDialogState;
  projectSearch: ProjectSearchState | null;
  openInputConfigCellId: CellId | null;
  editingLabelCellId: CellId | null;
  cellModal: CellModalState | null;
  cellSidebar: CellSidebarState | null;
  // Using a record like this is basically the same as using a set
  // but this works a little bit better with redux dev tools
  selectedCellIds: Record<CellId, true | undefined>;
  mostRecentlySelectedCellId: CellId | null;
  focusedView: EditViewType | null;
  /**
   * Prefer using flags on events such as `evtModKey(evt)`.
   * Only meant for accessing keyboard state in handlers without events.
   */
  holdingMod: boolean;
  /**
   * Prefer using flags on events such as `evt.shift`.
   * Only meant for accessing keyboard state in handlers without events.
   */
  holdingShift: boolean;
  currentlyEditingTabId: GridLayoutId | null;
  activeGridElementIds: GridElementId[];
  focusedCommentId: CommentId | null;
  focusedCommentLocation: CommentLocation | null;
  editMode: boolean;
  dataAppFileEditorFocused: boolean;
  versionHistorySelectedVersion: HexVersionString | null;
  importWarnings: WarningsFragment | null;
  componentImportDialog: ComponentImportDialogState;
  componentUpdateDialog: ComponentUpdateDialogState;
  createComponentSidebar: CreateComponentSidebar;

  actionBarState: ActionBarState;
  cellPromptBarState: CellPromptBarState;

  cellIdToMagicState: Record<CellId, CellMagicState | undefined>;
  cellUserModifiedDiffs: CellUserModifiedDiffState;

  previewState?: ProjectTemplateForUI | null;
  jumpState: JumpState;
  /**
   * We use three pieces of state to track the last used data connection, which are updated
   * any time a user selects a data connection via the `DataConnectionSelect` component
   *  - the most recently used "real" data connection ID (i.e. corresponding to a data warehouse,
   *     not dataframe SQL)
   *  - whether the most recent data connection selection was for dataframe SQL
   *  - whether the most recently used "real" data connection cell is in dataframe mode (true) or query mode (false)
   * These values are initialized on page load based on any existing SQL cells. If there are no
   * SQL cells, `lastUsedDataConnection` falls back to:
   *  - an arbitrarily selected project-level connection, or if none:
   *  - an arbitrarily selected workspace connection already imported to the project, or if none:
   *  - the org default connection, or if none:
   *  - an arbitrarily selected workspace connection
   */
  lastUsedDataConnection?: DataConnectionId;
  lastUsedDataframeSql: boolean;
  lastUsedLoadIntoDataFrame?: boolean;
  /**
   * Used in the review request modal to track the versions being compared.
   */
  diffingVersions: {
    newVersion: HexVersionString;
    oldVersion: HexVersionString;
  } | null;
  /**
   * In order to correctly handle project search view focus when the cmd+f hotkey is pressed, we use an incrementing counter in Redux
   * and subscribe to this counter's changes in SidebarWrapper.tsx.
   * Whenever we wish to focus on the project search, we will increment this counter.
   * If a user selects text in a cell and then opens the project search, we should
   * change the search to include that term.
   */
  projectSearchViewFocus: {
    counter: number;
    selectedText: string | null;
  };
  /**
   * Expand to replace when the replace hotkey is selected.
   */
  projectSearchReplaceViewCounter: number;
  /**
   * The cellId that we scrolled to from another action, such as from the Project search view.
   * When selecting a search item from project search, we should (1) scroll to the cell and (2)
   * show an animation that is similar to the selection/focus state for cells without hijacking focus.
   */
  scrolledToViewCellId: CellId | null;
  /**
   * Used to determine if we should render invisible drag targets to make DnD easier
   */
  cellDragging: boolean;
};

const initialLogicViewState: LogicViewSliceState = {
  embedDialog: {
    open: false,
    staticCellId: null,
  },
  deleteTabDialog: {
    open: false,
  },
  reviewRequestDialog: {
    status: "initial",
  },
  publishDialog: {
    view: "settings",
    versionName: "",
  },
  projectSearch: null,
  currentlyEditingTabId: null,
  activeGridElementIds: [],
  openInputConfigCellId: null,
  editingLabelCellId: null,
  cellModal: null,
  cellSidebar: null,
  selectedCellIds: {},
  mostRecentlySelectedCellId: null,
  focusedView: null,
  holdingMod: false,
  holdingShift: false,
  focusedCommentId: null,
  focusedCommentLocation: null,
  editMode: true,
  dataAppFileEditorFocused: false,
  versionHistorySelectedVersion: null,
  importWarnings: null,
  componentImportDialog: {
    open: false,
    selectedComponent: null,
    addCellConfig: {
      location: {
        type: "global",
      },
      language: ProjectLanguage.PYTHON,
    },
  },
  componentUpdateDialog: {
    open: false,
    componentUpdateVersions: undefined,
  },
  createComponentSidebar: {},

  actionBarState: {
    mode: ActionBarMode.ADD_CELL,
    location: {
      type: "global",
    },
    isOpen: false,
    disableCloseAnimation: false,
  },
  cellPromptBarState: {
    cellId: null,
  },

  cellIdToMagicState: {},
  cellUserModifiedDiffs: {},

  jumpState: {
    pos: 0,
    history: [],
    isPanelOpen: false,
  },
  lastUsedDataConnection: undefined,
  lastUsedDataframeSql: false,
  diffingVersions: null,
  projectSearchReplaceViewCounter: 0,
  projectSearchViewFocus: {
    counter: 0,
    selectedText: null,
  },
  scrolledToViewCellId: null,
  cellDragging: false,
};

const logicViewSlice = createSlice({
  name: "logicView",
  initialState: initialLogicViewState,
  reducers: {
    setLastUsedDataConnection: (
      state,
      action: PayloadAction<DataConnectionId | undefined>,
    ) => {
      state.lastUsedDataConnection = action.payload;
    },
    setLastUsedDataframeSql: (state, action: PayloadAction<boolean>) => {
      state.lastUsedDataframeSql = action.payload;
    },
    setLastUsedLoadIntoDataFrame: (state, action: PayloadAction<boolean>) => {
      state.lastUsedLoadIntoDataFrame = action.payload;
    },
    openEmbedDialog: (state, action: PayloadAction<StaticCellId | null>) => {
      state.embedDialog.open = true;
      state.embedDialog.staticCellId = action.payload;
    },
    closeEmbedDialog: (state, _action: PayloadAction) => {
      state.embedDialog.open = false;
      state.embedDialog.staticCellId = null;
    },
    setDeleteTabDialog: (
      state,
      action: PayloadAction<DeleteTabDialogState>,
    ) => {
      state.deleteTabDialog = action.payload;
    },
    setReviewRequestDialogStatus: (
      state,
      action: PayloadAction<
        "initial" | "loading" | { errorMessage: string } | "success"
      >,
    ) => {
      if (typeof action.payload === "string") {
        state.reviewRequestDialog.status = action.payload;
        delete state.reviewRequestDialog.errorMessage;
      } else {
        state.reviewRequestDialog.status = "error";
        state.reviewRequestDialog.errorMessage = action.payload.errorMessage;
      }
    },
    setPublishDialogView: (
      state,
      action: PayloadAction<PublishDialogState["view"]>,
    ) => {
      state.publishDialog.view = action.payload;
    },
    setPublishDialogVersionName: (state, action: PayloadAction<string>) => {
      state.publishDialog.versionName = action.payload;
    },
    setProjectSearch: (
      state,
      action: PayloadAction<ProjectSearchState | null>,
    ) => {
      state.projectSearch = action.payload;
    },
    setImportWarnings: (state, action: PayloadAction<WarningsFragment>) => {
      state.importWarnings = castDraft(action.payload);
    },
    clearImportWarnings: (state, _action: PayloadAction) => {
      state.importWarnings = null;
    },
    setOpenInputConfigCellId: (state, action: PayloadAction<CellId | null>) => {
      state.openInputConfigCellId = action.payload;
    },
    setEditingLabelCellId: (state, action: PayloadAction<CellId | null>) => {
      state.editingLabelCellId = action.payload;
    },
    setCurrentlyEditingTabId: (
      state,
      action: PayloadAction<GridLayoutId | null>,
    ) => {
      state.currentlyEditingTabId = action.payload;
    },
    setActiveGridElementIds: (
      state,
      action: PayloadAction<
        | GridElementId[]
        | ((currGridElementIds: GridElementId[]) => GridElementId[])
      >,
    ) => {
      if (isArray(action.payload)) {
        state.activeGridElementIds = action.payload;
      } else {
        const newGridElementIds = action.payload(state.activeGridElementIds);
        if (newGridElementIds !== state.activeGridElementIds) {
          state.activeGridElementIds = newGridElementIds;
        }
      }
    },
    setSelectedCellIds: (state, action: PayloadAction<CellId[]>) => {
      const newSelectionIds = new Set(action.payload);
      for (const cellId of typedObjectKeys(state.selectedCellIds)) {
        if (!newSelectionIds.has(cellId)) {
          delete state.selectedCellIds[cellId];
        }
      }
      for (const cellId of action.payload) {
        state.selectedCellIds[cellId] = true;
      }

      if (action.payload.length > 0) {
        state.mostRecentlySelectedCellId =
          action.payload[action.payload.length - 1];
      } else if (state.mostRecentlySelectedCellId != null) {
        state.mostRecentlySelectedCellId = null;
      }

      state.scrolledToViewCellId = null;
    },
    addSelectedCellIds: (state, action: PayloadAction<CellId[]>) => {
      for (const cellId of action.payload) {
        state.selectedCellIds[cellId] = true;
      }

      if (action.payload.length > 0) {
        state.mostRecentlySelectedCellId =
          action.payload[action.payload.length - 1];
      } else if (state.mostRecentlySelectedCellId != null) {
        state.mostRecentlySelectedCellId = null;
      }

      state.scrolledToViewCellId = null;
    },
    removeSelectedCellIds: (state, action: PayloadAction<CellId[]>) => {
      for (const cellId of action.payload) {
        if (state.selectedCellIds[cellId]) {
          delete state.selectedCellIds[cellId];
        }
      }
    },
    setMostRecentlySelectedCellId: (
      state,
      action: PayloadAction<CellId | null>,
    ) => {
      state.mostRecentlySelectedCellId = action.payload;
      state.scrolledToViewCellId = null;
    },
    setFocusedView: (state, action: PayloadAction<EditViewType | null>) => {
      state.focusedView = action.payload;
    },
    setHoldingMod: (state, action: PayloadAction<boolean>) => {
      state.holdingMod = action.payload;
    },
    setHoldingShift: (state, action: PayloadAction<boolean>) => {
      state.holdingShift = action.payload;
    },
    setCellModal: (state, action: PayloadAction<CellModalState | null>) => {
      state.cellModal = action.payload;
    },
    setCellSidebar: (state, action: PayloadAction<CellSidebarState | null>) => {
      state.cellSidebar = action.payload;
    },
    setFocusedCommentId: (state, action: PayloadAction<CommentId | null>) => {
      state.focusedCommentId = action.payload;
    },
    setFocusedCommentLocation: (
      state,
      action: PayloadAction<CommentLocation | null>,
    ) => {
      state.focusedCommentLocation = action.payload;
    },
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.editMode = action.payload;
    },
    setDataAppFileEditorFocused: (state, action: PayloadAction<boolean>) => {
      state.dataAppFileEditorFocused = action.payload;
    },
    setVersionHistorySelectedVersion: (
      state,
      action: PayloadAction<HexVersionString | null>,
    ) => {
      state.versionHistorySelectedVersion = action.payload;
    },
    openComponentImportDialog: (
      state,
      action: PayloadAction<ComponentImportAddCellConfig>,
    ) => {
      state.componentImportDialog.open = true;
      state.componentImportDialog.addCellConfig = action.payload;
    },
    closeComponentImportDialog: (state, _action: PayloadAction) => {
      state.componentImportDialog.open = false;
      state.componentImportDialog.selectedComponent = null;
      state.componentImportDialog.addCellConfig = {
        location: {
          type: "global",
        },
        language: ProjectLanguage.PYTHON,
      };
    },
    setSelectedComponent: (
      state,
      action: PayloadAction<ComponentVersionStub | null>,
    ) => {
      state.componentImportDialog.selectedComponent = action.payload;
    },
    openComponentUpdateDialog: (
      state,
      action: PayloadAction<ComponentUpdateVersions>,
    ) => {
      state.componentUpdateDialog.open = true;
      state.componentUpdateDialog.componentUpdateVersions = action.payload;
    },
    closeComponentUpdateDialog: (state, _action: PayloadAction) => {
      state.componentUpdateDialog.open = false;
    },
    openCreateComponentSidebar: (
      state,
      action: PayloadAction<HexVersionId>,
    ) => {
      const sidebarState = (state.createComponentSidebar[action.payload] ??= {
        open: true,
      });
      sidebarState.open = true;
    },
    closeCreateComponentSidebar: (
      state,
      action: PayloadAction<HexVersionId>,
    ) => {
      const sidebarState = (state.createComponentSidebar[action.payload] ??= {
        open: false,
      });
      sidebarState.open = false;
    },
    setSettingsForNewComponent: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        settings?: NewComponentSettings;
      }>,
    ) => {
      const sidebarState = (state.createComponentSidebar[
        action.payload.hexVersionId
      ] ??= {
        settings: action.payload.settings,
      });

      sidebarState.settings = action.payload.settings;
    },
    openActionBar: (
      state,
      action: PayloadAction<
        | {
            mode: ActionBarMode;
            location: CellLocation;
          }
        | undefined
      >,
    ) => {
      state.cellPromptBarState.cellId = null;
      state.actionBarState.isOpen = true;
      state.actionBarState.disableCloseAnimation = false;
      if (action.payload != null) {
        state.actionBarState.mode = action.payload.mode;
        state.actionBarState.location = action.payload.location;
      }
    },
    setActionBarMode: (state, action: PayloadAction<ActionBarMode>) => {
      state.actionBarState.mode = action.payload;
    },
    closeActionBar: (
      state,
      action: PayloadAction<
        | {
            /**
             * @default false
             */
            disableCloseAnimation: boolean;
          }
        | undefined
      >,
    ) => {
      state.actionBarState.isOpen = false;
      state.actionBarState.disableCloseAnimation =
        action.payload?.disableCloseAnimation ?? false;
    },
    openCellPromptBar: (
      state,
      action: PayloadAction<{
        cellId: CellId;
      }>,
    ) => {
      state.actionBarState.isOpen = false;
      state.cellPromptBarState.cellId = action.payload.cellId;
    },
    closeCellPromptBar: (state) => {
      state.cellPromptBarState.cellId = null;
    },
    setMagicExploreValidationLoading: (
      state,
      action: CellDataAction<boolean>,
    ) => {
      const magicCellState = (state.cellIdToMagicState[
        action.payload.cellId
      ] ??= createInitialMagicCellState());

      magicCellState.exploreValidationLoading = action.payload.data;
    },
    setMagicPrompt: (
      state,
      action: CellDataAction<RichTextDocument | undefined>,
    ) => {
      const magicCellState = (state.cellIdToMagicState[
        action.payload.cellId
      ] ??= createInitialMagicCellState());

      magicCellState.prompt = action.payload.data;
    },
    closeEditBar: (state, action: CellAction) => {
      const magicCellState = (state.cellIdToMagicState[
        action.payload.cellId
      ] ??= createInitialMagicCellState());

      magicCellState.smartEditType = undefined;
    },
    setSmartEditType: (
      state,
      action: CellDataAction<MagicKeyword | undefined>,
    ) => {
      const magicCellState = (state.cellIdToMagicState[
        action.payload.cellId
      ] ??= createInitialMagicCellState());

      magicCellState.smartEditType = action.payload.data;
    },
    setPreview: (state, action: PayloadAction<ProjectTemplateForUI | null>) => {
      state.previewState = action.payload;
    },
    pushJumpHistory: (state, action: PayloadAction<CellId>) => {
      const maxHistoryEntries = 100;

      // only push to jump history stack if the new cell is different from the last one
      const curLastCellId = state.jumpState.history[0];
      const newLastCellId = action.payload;
      if (curLastCellId !== newLastCellId) {
        state.jumpState.history = [
          action.payload,
          ...state.jumpState.history.slice(
            state.jumpState.pos,
            maxHistoryEntries - 1,
          ),
        ];
        state.jumpState.pos = 0;
      }
    },
    moveJumpHistory: (
      state,
      action: PayloadAction<{
        pos: number;
        history: CellId[];
      }>,
    ) => {
      state.jumpState.pos = Math.min(
        Math.max(action.payload.pos, 0),
        state.jumpState.history.length - 1,
      );
      state.jumpState.history = action.payload.history;
    },
    setJumpHistoryPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.jumpState.isPanelOpen = action.payload;
    },
    setDiffingVersions: (
      state,
      action: PayloadAction<{
        newVersion: HexVersionString;
        oldVersion: HexVersionString;
      } | null>,
    ) => {
      state.diffingVersions = action.payload;
    },
    focusProjectSearchInput: (
      state,
      action: PayloadAction<{
        selectedText: string | null;
      }>,
    ) => {
      state.projectSearchViewFocus = {
        counter: state.projectSearchViewFocus.counter + 1,
        selectedText: action.payload.selectedText,
      };
    },
    setScrolledToViewCellId: (state, action: PayloadAction<CellId | null>) => {
      state.scrolledToViewCellId = action.payload;
    },
    focusProjectSearchReplaceInput: (state) => {
      state.projectSearchReplaceViewCounter =
        state.projectSearchReplaceViewCounter + 1;
    },
    setCellDragging: (state, action: PayloadAction<boolean>) => {
      state.cellDragging = action.payload;
    },
    setUserModifiedDiff: (state, action: CellDataAction<string>) => {
      state.cellUserModifiedDiffs[action.payload.cellId] = action.payload.data;
    },
    clearUserModifiedDiff: (state, action: CellAction) => {
      delete state.cellUserModifiedDiffs[action.payload.cellId];
    },
  },
});
export const selectPreviewState = (
  state: RootState,
): ProjectTemplateForUI | null => state.logicView.previewState ?? null;
export const selectOpenInputConfigCellId = (state: RootState): CellId | null =>
  state.logicView.openInputConfigCellId;
export const selectEditingLabelCellId = (state: RootState): CellId | null =>
  state.logicView.editingLabelCellId;
export const selectCellModal = (state: RootState): CellModalState | null =>
  state.logicView.cellModal;
export const selectCellSidebar = (state: RootState): CellSidebarState | null =>
  state.logicView.cellSidebar;

const selectSelectedCellIds = (
  state: RootState,
): Record<CellId, true | undefined> => state.logicView.selectedCellIds;
const selectSelectedCellIdSet = createSelector(
  [selectSelectedCellIds],
  (selectedCellIdState: Record<CellId, true | undefined>) => {
    const selectedCellIds = new Set<CellId>();
    for (const [cellId, selected] of typedObjectEntries(selectedCellIdState)) {
      if (selected) {
        selectedCellIds.add(cellId);
      }
    }
    return selectedCellIds;
  },
);
const selectNumberOfCellsSelected = (state: RootState): number =>
  selectSelectedCellIdSet(state).size;
const selectIsCellSelected = (state: RootState, cellId: CellId): boolean =>
  selectSelectedCellIdSet(state).has(cellId);
const selectIsCellMultiselected = (state: RootState, cellId: CellId): boolean =>
  selectNumberOfCellsSelected(state) > 1 && selectIsCellSelected(state, cellId);

const selectSortedSelectedCells = createSelector(
  [
    selectSelectedCellIdSet,
    (state: RootState, hexVersionId: HexVersionId) =>
      hexVersionMPSelectors
        .getCellSelectors(hexVersionId)
        .selectFlattenedSorted(state),
  ],
  (selectedCellIds, sortedCells) =>
    sortedCells.filter(({ id }) => selectedCellIds.has(id)),
);

const selectSelectionIncludesComponentChild = createSelector(
  [selectSortedSelectedCells],
  (cells) => cells.some((c) => c.parentComponentImportCellId != null),
);

const selectSelectionOnlyIncludesComponentChildren = createSelector(
  [selectSortedSelectedCells],
  (cells) => cells.every((c) => c.parentComponentImportCellId != null),
);

export const cellSelectionSelectors = {
  selectSelectedCellIdSet,
  selectNumberOfCellsSelected,
  selectIsCellSelected,
  selectIsCellMultiselected,
  selectSortedSelectedCells,
  selectSelectionIncludesComponentChild,
  selectSelectionOnlyIncludesComponentChildren,
};

export const selectLastUsedDataConnection = (
  state: RootState,
): DataConnectionId | undefined => state.logicView.lastUsedDataConnection;

export const selectActionBarState = (state: RootState): ActionBarState =>
  state.logicView.actionBarState;
export const selectActionBarMode = (state: RootState): ActionBarMode =>
  selectActionBarState(state).mode;

const selectCellPromptBarState = (state: RootState): CellPromptBarState =>
  state.logicView.cellPromptBarState;
const selectMagicCellState = (
  state: RootState,
  cellId: CellId,
): CellMagicState =>
  state.logicView.cellIdToMagicState[cellId] ?? INITIAL_MAGIC_CELL_STATE;
const selectMagicExploreValidationLoading = (
  state: RootState,
  cellId: CellId,
): boolean =>
  state.logicView.cellIdToMagicState[cellId]?.exploreValidationLoading ?? false;
const selectUserModifiedDiff = (
  state: RootState,
  cellId: CellId,
): string | undefined => state.logicView.cellUserModifiedDiffs[cellId];

export const magicSelectors = {
  selectCellPromptBarState,
  selectMagicCellState,
  selectMagicExploreValidationLoading,
  selectUserModifiedDiff,
};

export const selectEditMode = (state: RootState): boolean =>
  state.logicView.editMode;
export const selectDataAppFileEditorFocused = (state: RootState): boolean =>
  state.logicView.dataAppFileEditorFocused;
export const selectVersionHistorySelectedVersion = (
  state: RootState,
): HexVersionString | null => state.logicView.versionHistorySelectedVersion;
export const selectEmbedDialogState = (state: RootState): EmbedDialogState =>
  state.logicView.embedDialog;
export const selectImportWarningsState = (
  state: RootState,
): WarningsFragment | null => state.logicView.importWarnings;
export const selectComponentImportDialogState = (
  state: RootState,
): ComponentImportDialogState => state.logicView.componentImportDialog;
export const selectReviewRequestDialogState = (
  state: RootState,
): ReviewRequestDialogState => state.logicView.reviewRequestDialog;
export const selectPublishDialogState = (
  state: RootState,
): PublishDialogState => state.logicView.publishDialog;
export const selectProjectSearchState = (
  state: RootState,
): ProjectSearchState | null => state.logicView.projectSearch;

export const selectComponentUpdateDialogState = (
  state: RootState,
): ComponentUpdateDialogState => state.logicView.componentUpdateDialog;

export const createComponentSidebarSelectors = memoize(
  (hexVersionId: HexVersionId) => {
    return {
      selectCreateComponentSidebarSettings: (state: RootState) => {
        return state.logicView.createComponentSidebar[hexVersionId]?.settings;
      },
      selectCreateComponentSidebarOpen: (state: RootState) => {
        return (
          state.logicView.createComponentSidebar[hexVersionId]?.open ?? false
        );
      },
    };
  },
);

export const selectCellDragging = (state: RootState): boolean =>
  state.logicView.cellDragging;

export const logicViewReducer = logicViewSlice.reducer;

export const magicActions = {
  closeCellPromptBar: logicViewSlice.actions.closeCellPromptBar,
  openCellPromptBar: logicViewSlice.actions.openCellPromptBar,
  setMagicPrompt: logicViewSlice.actions.setMagicPrompt,
  closeEditBar: logicViewSlice.actions.closeEditBar,
  setSmartEditType: logicViewSlice.actions.setSmartEditType,
  setMagicExploreValidationLoading:
    logicViewSlice.actions.setMagicExploreValidationLoading,
  setUserModifiedDiff: logicViewSlice.actions.setUserModifiedDiff,
  clearUserModifiedDiff: logicViewSlice.actions.clearUserModifiedDiff,
};

export const {
  addSelectedCellIds,
  clearImportWarnings,
  closeActionBar,
  closeComponentImportDialog,
  closeComponentUpdateDialog,
  closeCreateComponentSidebar,
  closeEmbedDialog,
  focusProjectSearchInput,
  focusProjectSearchReplaceInput,
  moveJumpHistory,
  openActionBar,
  openComponentImportDialog,
  openComponentUpdateDialog,
  openCreateComponentSidebar,
  openEmbedDialog,
  pushJumpHistory,
  removeSelectedCellIds,
  setActionBarMode,
  setActiveGridElementIds,
  setCellDragging,
  setCellModal,
  setCellSidebar,
  setCurrentlyEditingTabId,
  setDataAppFileEditorFocused,
  setDeleteTabDialog,
  setDiffingVersions,
  setEditMode,
  setEditingLabelCellId,
  setFocusedCommentId,
  setFocusedCommentLocation,
  setFocusedView,
  setHoldingMod,
  setHoldingShift,
  setImportWarnings,
  setJumpHistoryPanelOpen,
  setLastUsedDataConnection,
  setLastUsedDataframeSql,
  setLastUsedLoadIntoDataFrame,
  setMostRecentlySelectedCellId,
  setOpenInputConfigCellId,
  setPreview,
  setProjectSearch,
  setPublishDialogVersionName,
  setPublishDialogView,
  setReviewRequestDialogStatus,
  setScrolledToViewCellId,
  setSelectedCellIds,
  setSelectedComponent,
  setSettingsForNewComponent,
  setVersionHistorySelectedVersion,
} = logicViewSlice.actions;
